import * as Accordion from "@radix-ui/react-accordion";
import classNames from "classnames";
import React from "react";
import { rootProductSelector } from "tsi-common-react/src/apps/configurator/selectors";
import { useAppSelector } from "tsi-common-react/src/apps/reducers";
import { Image } from "tsi-common-react/src/common/Image";
import { IProduct } from "tsi-common-react/src/models/catalogue";
import { t } from "ttag";

import minus from "../../img/pdp/hotspot/minus.png";
import plus from "../../img/pdp/hotspot/plus.png";
import { IHotspotContent } from "../models";

import styles from "./Hotspot.module.scss";

const getTipIds = (hotspotContent: IHotspotContent): string[] => {
    return Object.keys(hotspotContent).filter((key) => key.includes("hotspot"));
};

const getRootClasses = (rootProduct: IProduct): string => {
    const pageLink = rootProduct.link?.toString();
    return classNames({
        [styles.root]: true,
        [styles.adapt]: rootProduct.slug.includes("adapt"),
        [styles.breeze]: pageLink?.includes("breeze-collection"),
        [styles.activeBreeze]: pageLink?.includes("active-breeze"),
        [styles.cloud]: rootProduct.slug.includes("cloud"),
        [styles.splitHeadKing]: rootProduct.slug.includes("split-head-king"),
        [styles.ergoSmartBase]: rootProduct.slug.includes("ergo-smart-base"),
    });
};

export const HotspotBlock = (props: {
    hotspotContent: IHotspotContent;
}): React.ReactNode => {
    const rootProduct = useAppSelector((state) =>
        rootProductSelector(state.configurator),
    );

    if (!rootProduct) {
        return null;
    }

    const tipIds = getTipIds(props.hotspotContent);
    const rootClasses = getRootClasses(rootProduct);

    return (
        <div className={rootClasses}>
            <div
                className={styles.header}
                dangerouslySetInnerHTML={{
                    __html: props.hotspotContent.header_content,
                }}
            ></div>
            <Accordion.Root
                className={styles.accordion}
                type="single"
                defaultValue={tipIds[1]}
                collapsible={true}
            >
                {tipIds.map((tipId: string) => (
                    <Accordion.Item
                        key={tipId}
                        value={tipId}
                        className={styles.accordionItem}
                    >
                        <Accordion.Header>
                            <Accordion.Trigger className={styles.button}>
                                <div className={styles.pulse}> </div>
                                {/* TODO(i18n): Alt text on aria-hidden="true" elements is redundant and shouldn't be translated. Consider removing alt attributes or using empty string alt="" */}
                                <Image
                                    lazy={false}
                                    src={plus}
                                    className={styles.buttonImageExpand}
                                    aria-hidden="true"
                                    alt={t`Hotspot Icon Expand`}
                                />
                                <Image
                                    lazy={false}
                                    src={minus}
                                    className={styles.buttonImageCollapse}
                                    aria-hidden="true"
                                    alt={t`Hotspot Icon Collapse`}
                                />
                            </Accordion.Trigger>
                        </Accordion.Header>
                        <Accordion.Content className={styles.accordionPanel}>
                            <div
                                dangerouslySetInnerHTML={{
                                    __html: props.hotspotContent[
                                        tipId as keyof IHotspotContent
                                    ],
                                }}
                            ></div>
                        </Accordion.Content>
                    </Accordion.Item>
                ))}
            </Accordion.Root>
        </div>
    );
};
